/**
 * T-44: the coding agent's progress reports while it works. When it moves to a new focus or finishes a
 * logical block of work, it writes one fenced block in a message to its T3 thread:
 *
 * ```factory-progress
 * { "headline": "Agent side done", "detail": "…", "steer": ["Leave Slack for a follow-up"] }
 * ```
 *
 * The machine's factory agent finds these while the run waits on the thread (agent/src/commands.ts,
 * t3.wait), the run engine posts them on the task as quiet comments (no notifications), at most one
 * every few minutes per run, and each `steer` suggestion shows as a button that posts an @AI builder
 * comment, which goes into the agent's running turn (T-41).
 */

export const FACTORY_PROGRESS_LANG = 'factory-progress';
/** The block a progress comment carries its steering suggestions in. */
export const FACTORY_STEER_LANG = 'factory-steer';

const HEADLINE_MAX = 120;
const DETAIL_MAX = 400;
const STEER_MAX = 160;
export const FACTORY_STEER_MAX = 3;

export interface FactoryProgress {
  headline: string;
  detail?: string;
  /** Up to FACTORY_STEER_MAX short suggestions a person can send back to steer the agent. */
  steer?: string[];
}

/** One report the machine found: `key` is where it was (message id and block), so it's posted once. */
export interface FactoryProgressReport {
  key: string;
  report: FactoryProgress;
}

const oneLine = (v: unknown, max: number): string => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, max) : '');

/** A report's fields, checked and trimmed; null when it has no headline. Accepts the parsed JSON or the object itself. */
export function cleanFactoryProgress(data: unknown): FactoryProgress | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const obj = data as Record<string, unknown>;
  const headline = oneLine(obj.headline ?? obj.title, HEADLINE_MAX);
  if (!headline) return null;
  const detail = oneLine(obj.detail ?? obj.summary, DETAIL_MAX);
  const raw = Array.isArray(obj.steer) ? obj.steer : [];
  const steer = [...new Set(raw.map((s) => oneLine(typeof s === 'string' ? s : (s as { label?: unknown } | null)?.label, STEER_MAX)).filter(Boolean))].slice(
    0,
    FACTORY_STEER_MAX
  );
  return { headline, ...(detail ? { detail } : {}), ...(steer.length ? { steer } : {}) };
}

export function parseFactoryProgressJson(raw: string): FactoryProgress | null {
  try {
    return cleanFactoryProgress(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * Every closed fenced block in `message` whose language is `lang`, in order. An unclosed one is left out:
 * the message may still be being written.
 */
export function closedFencedBlocks(message: string, lang: string): string[] {
  const lines = message.replace(/\r\n?/g, '\n').split('\n');
  const open = new RegExp('^ {0,3}(`{3,}|~{3,})[ \\t]*' + lang + '[ \\t]*$');
  const out: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const m = open.exec(lines[i]!);
    if (!m) continue;
    const fence = m[1]!;
    const close = new RegExp(`^ {0,3}${fence[0] === '`' ? '`' : '~'}{${fence.length},}[ \\t]*$`);
    let j = i + 1;
    while (j < lines.length && !close.test(lines[j]!)) j++;
    if (j >= lines.length) break;
    out.push(lines.slice(i + 1, j).join('\n'));
    i = j;
  }
  return out;
}

/** The usable factory-progress blocks in one agent message, keyed `<itemId>:<n>` (n from 1). */
export function findFactoryProgress(message: string, itemId: string): FactoryProgressReport[] {
  return closedFencedBlocks(message, FACTORY_PROGRESS_LANG).flatMap((body, i) => {
    const report = parseFactoryProgressJson(body);
    return report ? [{ key: `${itemId}:${i + 1}`, report }] : [];
  });
}

/** The steering suggestions a progress comment carries, from its factory-steer block's JSON. */
export function parseFactorySteerJson(raw: string): string[] | null {
  try {
    const data = JSON.parse(raw) as { options?: unknown };
    const options = Array.isArray(data?.options) ? data.options.map((o) => oneLine(o, STEER_MAX)).filter(Boolean).slice(0, FACTORY_STEER_MAX) : [];
    return options.length ? options : null;
  } catch {
    return null;
  }
}

/**
 * The comment for one or more reports (several when they came faster than the factory posts): each
 * report's headline in bold with its detail, then the newest report's steering suggestions, if any, as a
 * factory-steer block the task shows as buttons.
 */
export function formatProgressComment(reports: FactoryProgress[]): string {
  const parts = reports.map((r) => `**${r.headline.replace(/\*\*/g, '')}**${r.detail ? ` ${r.detail}` : ''}`);
  const steer = reports[reports.length - 1]?.steer;
  if (steer?.length) parts.push('```' + FACTORY_STEER_LANG + '\n' + JSON.stringify({ options: steer }) + '\n```');
  return parts.join('\n\n');
}
